import { UseFormSetValue } from 'react-hook-form';

import type { TemplateFormSchema } from '../validations/edit-template.validation';

import { GRANTEE } from '../constants/template-signatory-type.constant';
import { TemplateCategory } from '../enums/template.const';
import { clearStepThreeMapping } from '../utils/clear-step-three-mapping.util';
import { hasFile } from '../utils/has-file.util';

type RefLike<T> = { current: T };
type SignatoriesArray = TemplateFormSchema['stepTwo']['signatories'];

const signatoriesSetEqual = (a: SignatoriesArray, b: SignatoriesArray): boolean => {
  const arrA = a ?? [];
  const arrB = b ?? [];
  if (arrA === arrB) return true;
  if (arrA.length !== arrB.length) return false;
  return arrA.every((v) => arrB.includes(v));
};

export type ResetHandlerContext = {
  defaultValues: TemplateFormSchema;
  setStep: (step: number) => void;
  setValue: UseFormSetValue<TemplateFormSchema>;
};

export const handleCategoryChange = (
  next: TemplateFormSchema['stepOne']['category'],
  prev: RefLike<TemplateFormSchema['stepOne']['category']>,
  ctx: ResetHandlerContext,
) => {
  if (prev.current === next) return;
  prev.current = next;
  const { defaultValues, setStep, setValue } = ctx;
  setValue('stepOne.subCategory', undefined);
  setValue('stepOne.equityPlan', '');
  setValue('stepThree', { ...defaultValues.stepThree, fileParsed: false });
  if (next === TemplateCategory.GRANT) {
    setValue('stepTwo.signatories', [GRANTEE]);
  }
  setStep(1);
};

export const handleSubCategoryChange = (
  next: TemplateFormSchema['stepOne']['subCategory'],
  prev: RefLike<TemplateFormSchema['stepOne']['subCategory']>,
  ctx: ResetHandlerContext,
) => {
  if (prev.current === next) return;
  prev.current = next;
  const { setValue } = ctx;
  setValue('stepOne.equityPlan', '');
  clearStepThreeMapping(setValue);
};

export const handleEquityPlanChange = (
  _next: TemplateFormSchema['stepOne']['equityPlan'],
  prev: RefLike<TemplateFormSchema['stepOne']['equityPlan']>,
  ctx: ResetHandlerContext,
) => {
  if (prev.current === _next) return;
  prev.current = _next;
  const { setValue } = ctx;
  clearStepThreeMapping(setValue);
};

export const handleStepThreeFileChange = (
  next: unknown,
  prevFile: RefLike<unknown>,
  ctx: Pick<ResetHandlerContext, 'setValue'>,
) => {
  const hadFile = hasFile(prevFile.current);
  const nowHasFile = hasFile(next);
  prevFile.current = next;
  const { setValue } = ctx;
  if (nowHasFile) {
    setValue('stepThree.fileParsed', false);
    if (hadFile) {
      setValue('stepThree.fieldMapping', {});
      setValue('stepThree.fieldMappingTypes', {});
      setValue('stepThree.detectedVariables', []);
      setValue('stepThree.tabs', []);
      setValue('stepThree.mergeFieldIssues', []);
    }
  } else if (hadFile) {
    setValue('stepThree.fileParsed', false);
  }
};

export const handleSignatoriesChange = (
  next: SignatoriesArray,
  prev: RefLike<SignatoriesArray>,
  ctx: Pick<ResetHandlerContext, 'setValue'>,
) => {
  if (signatoriesSetEqual(prev.current, next)) return;
  prev.current = next;
  clearStepThreeMapping(ctx.setValue);
};
